import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { shadowLg } from '../../../../web/styles/shadows';

const { colors, spacing, common, typography } = THEME;

export const styleSheet = StyleSheet.create({
	// Override DialogPopup positioning to slide-in from the right.
	// The Dialog primitive applies its own `dialogContentDefault` (center-anchored)
	// styles; these win because aphrodite cascades last-declared.
	panel: {
		left: 'auto',
		right: 0,
		top: 0,
		translate: 'none',
		height: '100vh',
		width: '100%',
		maxWidth: 520,
		borderRadius: 0,
		padding: 0,
		boxShadow: shadowLg,
	},
	innerBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.lg,
		padding: spacing.lg,
		overflowY: 'auto',
		flexGrow: 1,
	},
	header: {
		padding: `${spacing.lg}px ${spacing.lg}px ${spacing.md}px`,
		borderBottom: common.border,
	},
	title: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.lg,
		fontWeight: typography.fontWeights.bold,
		color: colors.text.main,
		margin: 0,
	},
	subtitle: {
		fontFamily: 'monospace',
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
		marginTop: spacing.xs,
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},
	sectionTitle: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.md,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.main,
		margin: 0,
	},
	sectionDescription: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xs,
		lineHeight: 1.5,
		margin: 0,
	},
	statusLines: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
	},
	statusLine: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		fontSize: typography.fontSizes.sm,
	},
	statusDotOn: {
		display: 'inline-block',
		width: 10,
		height: 10,
		borderRadius: '50%',
		background: colors.success.main,
	},
	statusDotOff: {
		display: 'inline-block',
		width: 10,
		height: 10,
		borderRadius: '50%',
		background: colors.icon.disabled,
	},
	adAccountList: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		padding: spacing.sm,
		background: colors.background.main,
		border: common.border,
		borderRadius: common.borderRadius,
	},
	adAccountRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: spacing.sm,
		padding: spacing.xs,
		fontSize: typography.fontSizes.sm,
	},
	adAccountLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		flex: 1,
		minWidth: 0,
	},
	adAccountId: {
		fontFamily: 'monospace',
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
		flexShrink: 0,
	},
	adAccountName: {
		color: colors.text.main,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	// Compact override for the destructive-secondary `Button` used as a per-row
	// Remove action; keeps the row compact next to the status Pill.
	removeButton: {
		fontSize: typography.fontSizes.xs,
		paddingTop: spacing.xs,
		paddingBottom: spacing.xs,
		paddingLeft: spacing.sm,
		paddingRight: spacing.sm,
		minHeight: 'auto',
		flexShrink: 0,
	},
	emptyHint: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		fontStyle: 'italic',
		padding: spacing.sm,
		textAlign: 'center',
	},
	metricsSyncRow: {
		display: 'flex',
		gap: spacing.sm,
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
	},
	metricsSyncLabel: {
		color: colors.text.light,
		minWidth: 90,
	},
	metricsSyncId: {
		fontFamily: 'monospace',
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
	},
	metricsSyncErrorBox: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		padding: spacing.sm,
		background: colors.danger.light,
		border: `1px solid ${colors.danger.main}`,
		borderRadius: common.borderRadius,
	},
	metricsSyncMessage: {
		color: colors.danger.dark,
		fontSize: typography.fontSizes.xs,
		fontFamily: 'monospace',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		margin: 0,
	},
	pickerScroll: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		maxHeight: 320,
		overflowY: 'auto',
	},
	pickerFooter: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
	},
	pickerCount: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xs,
	},
	pickerCloseButton: {
		marginLeft: 'auto',
	},
	pickerWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		padding: spacing.sm,
		background: colors.background.main,
		border: common.border,
		borderRadius: common.borderRadius,
	},
	// Overrides for the `link` Button variant used as a clickable picker row —
	// flatten the button styling so the row reads as a list entry.
	pickerRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		gap: spacing.sm,
		padding: spacing.xs,
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
		textAlign: 'left',
		minHeight: 'auto',
		width: '100%',
		':hover': { background: colors.hover.light, textDecoration: 'none' },
	},
	errorBanner: {
		color: colors.danger.dark,
		background: colors.danger.light,
		padding: spacing.sm,
		borderRadius: common.borderRadius,
		fontSize: typography.fontSizes.sm,
	},
	skeletonStack: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},
	skeletonBar: {
		background: colors.background.main,
		borderRadius: common.borderRadius,
		height: 14,
	},
	skeletonBarSm: { width: '40%' },
	skeletonBarMd: { width: '55%' },
	skeletonBarLg: { width: '70%' },
});
